import React, { useState } from 'react';

import { Link as GatsbyLink } from 'gatsby';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { isComplainable } from '@printdeal/business-logic';

import {
  Icon,
  Panel,
  Link,
  LinkAsButton,
  Grid,
} from '@printdeal/ui-library';
import { Money } from '../../../../Money';
import ComplaintOrderLineItem from './ComplaintOrderLineItem';
import Order from '../../../../../types/order/Order';
import LineItem from '../../../../../types/cart/LineItem';
import { useSanitizedOrder } from '../../hooks/useOrders';

const ComplaintOrderSearchOrder = ({ order }: { order: Order }) => {
  const [lineItemsShown, setLineItemsShown] = useState(false);
  const toggleLineItemsShown = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setLineItemsShown(!lineItemsShown);
  };
  const { locale } = useIntl();

  const { order: orderData } = useSanitizedOrder(
    order.id,
    locale,
    'complaint_order_search_order_api_failure',
  );

  if (!orderData) {
    return null;
  }

  const {
    id, orderNumber, taxedPrice, totalPrice, lineItems,
  } = orderData;

  const orderLineItems: LineItem[] | undefined = orderData?.lineItems.filter((lineItem) => isComplainable(lineItem));
  // Make sure things still work if there is no taxed price
  const price = taxedPrice ? taxedPrice.totalGross : totalPrice;

  if (orderLineItems.length === 0 || lineItems.length === 0) {
    return null;
  }

  return (
    <Panel className="pd-relative">
      <div className="pd-flex pd-flex-col pd-gap-4">
        <Grid className="pd-grid-cols-12 pd-gap-x-4 pd-gap-y-1">
          <Grid.Item className="pd-order-1 pd-col-span-7 lg:pd-col-span-4">
            <Link
              to={id}
              as={GatsbyLink}
              variant="secondary"
              className="pd-font-bold after:pd-absolute after:pd-inset-0 after:pd-z-0 after:pd--m-px after:pd-rounded-md after:pd-border-primary-500 after:pd-content-[''] after:hover:pd-border-1"
            >
              <FormattedMessage id="orders.list.orderNumberPrefix" defaultMessage="Order" />
              {` ${orderNumber}`}
            </Link>
          </Grid.Item>
          <Grid.Item className="pd-order-4 pd-col-span-12 pd-text-dim-500 lg:pd-order-2 lg:pd-col-span-4">
            <FormattedDate
              value={order.createdAt}
              year="numeric"
              month="long"
              day="2-digit"
            />
          </Grid.Item>
          <Grid.Item className="pd-order-2 pd-col-span-4 pd-text-right pd-font-bold lg:pd-order-3 lg:pd-col-span-3">
            <Money value={price.centAmount} currency={price.currencyCode} />
          </Grid.Item>
          <Grid.Item className="pd-order-3 pd-col-span-1 pd-flex pd-justify-end pd-text-right lg:pd-order-4">
            <Icon size="md" icon="chevron-right" />
          </Grid.Item>
        </Grid>

        <div className="pd-z-10 pd-shrink pd-justify-self-start">
          <LinkAsButton
            type="button"
            variant="secondary"
            iconBefore={`double-chevron-${lineItemsShown ? 'up' : 'down'}`}
            onClick={toggleLineItemsShown}
            className="pd-text-xs hover:pd-text-primary-600"
          >
            {lineItemsShown ? (
              <FormattedMessage
                id="order.lineItems.hide"
                defaultMessage="Hide products ({productCount})"
                values={{ productCount: lineItems.length }}
              />
            ) : (
              <FormattedMessage
                id="order.lineItems.show"
                defaultMessage="Show products ({productCount})"
                values={{ productCount: lineItems.length }}
              />
            )}
          </LinkAsButton>
        </div>

        {lineItemsShown && (
          <div className="pd-flex pd-flex-col pd-gap-4">
            {
              lineItems.map((lineItem) => (
                <React.Fragment key={lineItem.id}>
                  <ComplaintOrderLineItem lineItem={lineItem} />
                </React.Fragment>
              ))
            }
          </div>
        )}
      </div>
    </Panel>
  );
};

export default ComplaintOrderSearchOrder;
